export interface PlatformLanguageConfig {
    enabledLanguages: string[];
    defaultLanguage: string;
    timestamp: number;
}

export class LanguageService {
    private static readonly CACHE_KEY = "foodpilot_languages";
    private static readonly USER_PREFERENCE_KEY = "foodpilot_user_language";

    static getCurrentLanguage(): string | null {
        const userPreference = this.getUserPreference();
        if (userPreference) {
            return userPreference;
        }

        const cached = this.getCachedConfig();
        if (cached) {
            return this.calculateOptimalLanguage(cached);
        }

        return null;
    }

    static calculateOptimalLanguage(config: PlatformLanguageConfig): string {
        // Browser languages (RFC 4647)
        const browserLanguages = navigator.languages || [navigator.language];

        for (const browserLang of browserLanguages) {
            if (!browserLang) continue;

            if (config.enabledLanguages.includes(browserLang.toLowerCase())) {
                return browserLang.toLowerCase();
            }

            const langCode = browserLang.split("-")[0].toLowerCase();
            if (config.enabledLanguages.includes(langCode)) {
                return langCode;
            }
        }

        return config.defaultLanguage || "en";
    }

    /**
     * 💾 Sauvegarder la config API en cache
     */
    static saveToCache(apiResponse: { enabledLanguages: string[]; defaultLanguage: string }): void {
        const cacheData: PlatformLanguageConfig = {
            enabledLanguages: apiResponse.enabledLanguages || [],
            defaultLanguage: apiResponse.defaultLanguage || "en",
            timestamp: Date.now(),
        };

        try {
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
            console.info("✅ Language config cached:", cacheData);
        } catch (error) {
            console.warn("Failed to cache language config:", error);
        }
    }

    static getCachedConfig(): PlatformLanguageConfig | null {
        try {
            const cached = localStorage.getItem(this.CACHE_KEY);
            if (!cached) return null;

            const parsed = JSON.parse(cached) as PlatformLanguageConfig;

            if (!parsed.enabledLanguages || !parsed.defaultLanguage) {
                this.clearCache();
                return null;
            }

            return parsed;
        } catch (error) {
            console.warn("Cache parsing failed:", error);
            this.clearCache();
            return null;
        }
    }

    static changeLanguage(languageCode: string): void {
        if (!languageCode) return;

        this.setUserPreference(languageCode);

        const htmlElement = document.documentElement;
        htmlElement.setAttribute("lang", languageCode);

        window.location.reload();
    }

    static getUserPreference(): string | null {
        try {
            return localStorage.getItem(this.USER_PREFERENCE_KEY);
        } catch {
            return null;
        }
    }

    static setUserPreference(languageCode: string): void {
        try {
            localStorage.setItem(this.USER_PREFERENCE_KEY, languageCode);
        } catch (error) {
            console.warn("Failed to save user language preference:", error);
        }
    }

    static clearCache(): void {
        try {
            localStorage.removeItem(this.CACHE_KEY);
        } catch (error) {
            console.warn("Failed to clear language cache:", error);
        }
    }
}
